import { Blueprint } from "@/components/builder/base/blueprints/Blueprint";
import { ValidatableBlueprint } from "@/components/builder/base/blueprints/ValidatableBlueprint";
import { HasWidth } from "@/components/builder/base/traits/HasWidth";
import { ValidationErrors } from "@/components/builder/base/types/ValidationErrors";
import { FormBuilderContract } from "@/components/builder/form";
import { BlueprintDefinition } from "@/components/builder/form/blueprints/BlueprintDefinition";
import { entry, Entry, instanceOfEntry } from "@/components/builder/form/entries/Entry";
import { ValidEntry } from "@/components/builder/form/entries/ValidEntry";
import { EntryFactory } from "@/components/builder/form/traits/EntryFactory";
import { HasFilter } from "@/components/builder/form/traits/HasFilter";
import { HasLabel } from "@/components/builder/form/traits/HasLabel";
import { IsUnique } from "@/components/builder/form/traits/IsUnique";
import { ProcessCallback } from "@/components/builder/form/types/ProcessCallback";
import { $t } from "@/plugins/localization";
import { DateTime } from "luxon";

export const Definition: BlueprintDefinition = {
    type: 'publication',
    name: '[[[Publikacja]]]',
    icon: 'fa-megaphone',
    group: 'cms',
    position: 10
};

export class PublicationData
{
    public datePublishedUtc?: DateTime = null;
    public dateDismissedUtc?: DateTime = null;
    public publications: PagePublicationModel[] = [];
}

export interface PagePublicationModel
{
    pageId: number;
    websiteId: number;
    locale: string;
    basePath: string;
}

export class PublicationEntry extends ValidEntry<PublicationData>
{
    public type: string = Definition.type;
    public data: PublicationData = {publications: []};

    public constructor(data: any = null)
    {
        super();

        if (data !== null)
        {
            this.data = {
                datePublishedUtc: data.datePublishedUtc,
                dateDismissedUtc: data.dateDismissedUtc,
                publications: data.publications || []
            };
        }
    }

    public async collect(blueprint: PublicationContract, form: FormBuilderContract, preprocess: ProcessCallback): Promise<Entry>
    {
        const result = await preprocess(blueprint, this, form.blueprintId, form.entryId);

        return entry({
            type: this.type,
            data: {
                datePublishedUtc: this.data.datePublishedUtc,
                dateDismissedUtc: this.data.dateDismissedUtc,
                publications: this.data.publications || []
            },
            ...(result ?? {})
        });
    }

    public validate(blueprint: PublicationContract, form: FormBuilderContract): boolean
    {
        this.errors = {};

        if (this.data.dateDismissedUtc !== null && this.data.dateDismissedUtc < this.data.datePublishedUtc)
        {
            this.errors.dateDismissedUtc = [ $t('[[[Data zakończenia publikacji nie może być wcześniejsza, niż jej rozpoczęcie]]]')];
        }

        return this.valid();
    }
}

export const instanceOfPublicationEntry = (object: any): object is PublicationEntry =>
{
    return instanceOfEntry(object) && 'type' in object && object.type === Definition.type;
};

export interface PublicationContract extends Blueprint, HasWidth, HasLabel, HasFilter, IsUnique
{
}

export class PublicationType implements PublicationContract, EntryFactory<PublicationEntry>
{
    public id: string;
    public type: string;
    public name: string;
    public minWidth: number;
    public width: number;
    public label: Record<string, string>;
    public showLabel: boolean;
    public showFilter: boolean;
    public isUnique: boolean;

    public constructor(id: string, name: string)
    {
        this.id = id;
        this.type = Definition.type;
        this.name = 'studioPublication';
        this.minWidth = 6;
        this.width = 6;
        this.label = { 'pl-PL': 'Publikacja', 'en-US': 'Publication' };
        this.showLabel = false;
        this.showFilter = true;
        this.isUnique = true;
    }

    public setDefaultWidth(width: number): void
    {
        this.width = 6;
    }

    public createEntry(data: any): PublicationEntry
    {
        return new PublicationEntry(data);
    }
}
